import type {
  GitHubRepoAutomationSettings,
  SlackChannelRouteSummary,
  SlackChannelSummary,
  SlackWorkspaceSettings,
} from '@scrambl/shared'
import {
  CreateIntegrationConnectionDto,
  IntegrationConnection,
  IntegrationProviderConfig,
  IntegrationScope,
  IntegrationSyncConfiguration,
  IntegrationSyncLog,
  UpdateIntegrationConnectionDto,
} from '@scrambl/shared'

import { withDesktopOAuthParams } from '@/lib/utils/oauth-connect'

import { apiClient, emptyResponseToNull } from './api-client'

export interface CalendarProviderCalendar {
  id: string
  summary: string
  primary?: boolean
  backgroundColor?: string
  isSelected: boolean
}

// One connected Google/Microsoft account with its calendars
export interface CalendarProviderAccount {
  connectionId: string
  name: string
  accountEmail: string | null
  status: string
  lastSyncAt?: string | null
  calendars: CalendarProviderCalendar[]
}

export interface SlackWorkspaceConnection {
  id: string
  organizationId: string
  slackTeamId: string
  slackTeamName: string
  slackEnterpriseId: string | null
  botUserId: string | null
  status: 'ACTIVE' | 'REVOKED' | 'DISABLED'
  scopes: string[]
  settings: SlackWorkspaceSettings & Record<string, unknown>
  hasBotAccessToken: boolean
  missingScopes: string[]
  needsReauth: boolean
  installedAt: string
  updatedAt: string
}

export interface SlackUserLinkConnection {
  id: string
  organizationId: string
  slackWorkspaceId: string
  slackTeamId: string
  slackUserId: string
  scramblUserId: string
  status: 'ACTIVE' | 'REVOKED'
  linkedAt: string
  lastVerifiedAt: string | null
  preferredOpenTarget: 'auto' | 'app' | 'web'
}

export interface GitHubInstallationConnection {
  id: string
  organizationId: string
  installationId: string
  appId: string | null
  accountId: string
  accountLogin: string
  accountType: 'USER' | 'ORGANIZATION'
  repositorySelection: string | null
  permissions: Record<string, unknown>
  events: string[]
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'
  installedAt: string
  updatedAt: string
  repositories?: GitHubRepositoryConnection[]
}

export interface GitHubRepositoryConnection {
  id: string
  organizationId: string
  installationId: string
  githubRepoId: string
  owner: string
  name: string
  fullName: string
  defaultBranch: string
  isPrivate: boolean
  isActive: boolean
  visibility: 'PUBLIC' | 'PRIVATE' | 'INTERNAL'
  htmlUrl: string | null
  lastSyncedAt: string | null
  installation?: GitHubInstallationConnection
  projectLinks?: Array<{
    id: string
    projectId: string
    isDefault: boolean
    project?: {
      id: string
      name: string
      code: string | null
    }
  }>
}

export interface GitHubUserConnection {
  id: string
  externalAccountId: string | null
  accountEmail: string | null
  connected: boolean
  login?: string
  name?: string
  avatarUrl?: string
  connectedAt?: string
  status: string
  errorMessage: string | null
}

export interface GitHubRepositoryUserConnections {
  connections: GitHubUserConnection[]
  preferredConnectionId: string | null
  userAuthConfigured: boolean
}

export interface ProjectGitHubRepositoryConnection {
  id: string
  organizationId: string
  projectId: string
  repositoryId: string
  isDefault: boolean
  defaultBaseBranch: string | null
  branchNamePattern: string
  prTitleTemplate: string
  prBodyTemplate: string
  automationSettings: GitHubRepoAutomationSettings | null
  repository: GitHubRepositoryConnection
}

export interface TaskGitHubLinkConnection {
  id: string
  organizationId: string
  taskId: string
  repositoryId: string
  projectRepositoryId: string | null
  branchName: string | null
  branchUrl: string | null
  pullRequestNumber: number | null
  pullRequestUrl: string | null
  pullRequestState: 'OPEN' | 'CLOSED' | 'MERGED' | null
  pullRequestTitle: string | null
  headRef: string | null
  baseRef: string | null
  isDraft: boolean
  status: string
  repository?: GitHubRepositoryConnection
  projectRepository?: ProjectGitHubRepositoryConnection
}

export const integrationsApi = {
  // Available integrations
  getAvailableIntegrations: (
    scope?: IntegrationScope,
  ): Promise<IntegrationProviderConfig[]> =>
    apiClient(`/integrations/available${scope ? `?scope=${scope}` : ''}`),

  // Organization connections
  getOrganizationConnections: (): Promise<IntegrationConnection[]> =>
    apiClient('/integrations/organization'),

  // User connections
  getUserConnections: (): Promise<IntegrationConnection[]> =>
    apiClient('/integrations/user'),

  // Single connection
  getConnection: (id: string): Promise<IntegrationConnection> =>
    apiClient(`/integrations/${id}`),

  // CRUD operations
  createConnection: (
    data: CreateIntegrationConnectionDto,
  ): Promise<IntegrationConnection> =>
    apiClient('/integrations', {
      method: 'POST',
      body: data,
    }),

  updateConnection: (
    id: string,
    data: UpdateIntegrationConnectionDto,
  ): Promise<IntegrationConnection> =>
    apiClient(`/integrations/${id}`, {
      method: 'PUT',
      body: data,
    }),

  deleteConnection: (id: string): Promise<{ success: boolean }> =>
    apiClient(`/integrations/${id}`, {
      method: 'DELETE',
    }),

  // Connection operations
  testConnection: (id: string): Promise<{ status: string; timestamp: Date }> =>
    apiClient(`/integrations/${id}/test`, {
      method: 'POST',
    }),

  // Sync operations
  getSyncLogs: (
    connectionId: string,
    limit?: number,
  ): Promise<IntegrationSyncLog[]> =>
    apiClient(
      `/integrations/${connectionId}/sync-logs${limit ? `?limit=${limit}` : ''}`,
    ),

  getSyncConfigurations: (
    connectionId: string,
  ): Promise<IntegrationSyncConfiguration[]> =>
    apiClient(`/integrations/${connectionId}/sync-configurations`),

  updateSyncConfiguration: (
    connectionId: string,
    configId: string,
    updates: Partial<{
      isEnabled: boolean
      syncInterval: number
      settings: Record<string, any>
      fieldMappings: Record<string, any>
      filters: Record<string, any>
    }>,
  ): Promise<IntegrationSyncConfiguration> =>
    apiClient(`/integrations/${connectionId}/sync-configurations/${configId}`, {
      method: 'PUT',
      body: updates,
    }),

  // QuickBooks specific endpoints
  quickbooks: {
    initiateAuth: async (
      connectionId: string,
    ): Promise<{ authUrl: string }> =>
      apiClient(
        await withDesktopOAuthParams('/integrations/quickbooks/auth/initiate'),
        {
          method: 'POST',
          body: { connectionId },
        },
      ),

    disconnect: (connectionId: string): Promise<void> =>
      apiClient(`/integrations/quickbooks/${connectionId}/disconnect`, {
        method: 'POST',
      }),

    triggerSync: (connectionId: string, entityTypes?: string[]): Promise<any> =>
      apiClient(`/integrations/quickbooks/${connectionId}/sync`, {
        method: 'POST',
        body: { entityTypes },
      }),

    getCustomers: (connectionId: string): Promise<any[]> =>
      apiClient(`/integrations/quickbooks/${connectionId}/customers`),

    getItems: (connectionId: string): Promise<any[]> =>
      apiClient(`/integrations/quickbooks/${connectionId}/items`),

    getAccounts: (connectionId: string): Promise<any[]> =>
      apiClient(`/integrations/quickbooks/${connectionId}/accounts`),

    getCustomerLinks: (connectionId: string): Promise<any[]> =>
      apiClient(`/integrations/quickbooks/${connectionId}/customer-links`),

    getTimeActivities: (
      connectionId: string,
      startDate?: string,
      endDate?: string,
    ): Promise<any[]> => {
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      const queryString = params.toString()

      return apiClient(
        `/integrations/quickbooks/${connectionId}/time-activities${queryString ? `?${queryString}` : ''}`,
      )
    },

    exportTimeEntries: (
      connectionId: string,
      timeEntryIds: string[],
      options?: {
        billableStatus?: 'Billable' | 'NotBillable'
        customerId?: string
        itemId?: string
      },
    ): Promise<any> =>
      apiClient(
        `/integrations/quickbooks/${connectionId}/export-time-entries`,
        {
          method: 'POST',
          body: { timeEntryIds, options },
        },
      ),

    getEntityCustomerLink: async (
      connectionId: string,
      entityId: string,
    ): Promise<any | null> =>
      emptyResponseToNull(
        await apiClient<any | null>(
          `/integrations/quickbooks/${connectionId}/entities/${entityId}/customer-link`,
        ),
      ),

    linkEntityCustomer: (
      connectionId: string,
      entityId: string,
      customerId: string,
    ): Promise<any> =>
      apiClient(
        `/integrations/quickbooks/${connectionId}/entities/${entityId}/customer-link`,
        {
          method: 'POST',
          body: { customerId },
        },
      ),

    unlinkEntityCustomer: (
      connectionId: string,
      entityId: string,
    ): Promise<{ success: boolean }> =>
      apiClient(
        `/integrations/quickbooks/${connectionId}/entities/${entityId}/customer-link`,
        {
          method: 'DELETE',
        },
      ),
  },

  // Google Calendar specific endpoints
  googleCalendar: {
    getAuthUrl: async (orgSlug?: string): Promise<{ authUrl: string }> =>
      apiClient(
        await withDesktopOAuthParams(
          `/integrations/google-calendar/auth/url${orgSlug ? `?orgSlug=${orgSlug}` : ''}`,
        ),
      ),

    handleCallback: (
      code: string,
      state: string,
    ): Promise<{ success: boolean; message: string }> =>
      apiClient('/integrations/google-calendar/auth/callback', {
        method: 'POST',
        body: { code, state },
      }),

    getConnection: (): Promise<IntegrationConnection> =>
      apiClient('/integrations/google-calendar/connection'),

    getAccounts: (): Promise<CalendarProviderAccount[]> =>
      apiClient('/integrations/google-calendar/accounts'),

    disconnect: (connectionId?: string): Promise<void> =>
      apiClient(
        `/integrations/google-calendar/connection${connectionId ? `?connectionId=${connectionId}` : ''}`,
        {
          method: 'DELETE',
        },
      ),

    getCalendars: (): Promise<
      Array<{
        id: string
        summary: string
        primary?: boolean
        backgroundColor?: string
        isSelected: boolean
      }>
    > => apiClient('/integrations/google-calendar/calendars'),

    updateCalendarSelection: (
      selectedCalendarIds: string[],
      connectionId?: string,
    ): Promise<{ success: boolean }> =>
      apiClient('/integrations/google-calendar/calendars/selection', {
        method: 'PUT',
        body: { selectedCalendarIds, connectionId },
      }),

    syncCalendar: (
      connectionId?: string,
    ): Promise<{
      success: boolean
      totalEvents: number
      successCount: number
      errorCount: number
    }> =>
      apiClient(
        `/integrations/google-calendar/sync${connectionId ? `?connectionId=${connectionId}` : ''}`,
        {
          method: 'POST',
        },
      ),

    getSyncStatus: (): Promise<{
      lastSyncAt?: Date
      lastSyncStatus?: string
      lastSyncError?: string
      isActive: boolean
    }> => apiClient('/integrations/google-calendar/sync/status'),
  },

  // Outlook Calendar specific endpoints
  outlookCalendar: {
    getAuthUrl: async (orgSlug?: string): Promise<{ authUrl: string }> =>
      apiClient(
        await withDesktopOAuthParams(
          `/integrations/outlook-calendar/auth/url${orgSlug ? `?orgSlug=${orgSlug}` : ''}`,
        ),
      ),

    handleCallback: (
      code: string,
      state: string,
    ): Promise<{ success: boolean; message: string }> =>
      apiClient('/integrations/outlook-calendar/auth/callback', {
        method: 'POST',
        body: { code, state },
      }),

    getConnection: (): Promise<IntegrationConnection> =>
      apiClient('/integrations/outlook-calendar/connection'),

    getAccounts: (): Promise<CalendarProviderAccount[]> =>
      apiClient('/integrations/outlook-calendar/accounts'),

    disconnect: (connectionId?: string): Promise<void> =>
      apiClient(
        `/integrations/outlook-calendar/connection${connectionId ? `?connectionId=${connectionId}` : ''}`,
        {
          method: 'DELETE',
        },
      ),

    getCalendars: (): Promise<
      Array<{
        id: string
        summary: string
        primary?: boolean
        backgroundColor?: string
        isSelected: boolean
      }>
    > => apiClient('/integrations/outlook-calendar/calendars'),

    updateCalendarSelection: (
      selectedCalendarIds: string[],
      connectionId?: string,
    ): Promise<{ success: boolean }> =>
      apiClient('/integrations/outlook-calendar/calendars/selection', {
        method: 'PUT',
        body: { selectedCalendarIds, connectionId },
      }),

    syncCalendar: (
      connectionId?: string,
    ): Promise<{
      success: boolean
      totalEvents: number
      successCount: number
      errorCount: number
    }> =>
      apiClient(
        `/integrations/outlook-calendar/sync${connectionId ? `?connectionId=${connectionId}` : ''}`,
        {
          method: 'POST',
        },
      ),

    getSyncStatus: (): Promise<{
      lastSyncAt?: Date
      lastSyncStatus?: string
      lastSyncError?: string
      isActive: boolean
    }> => apiClient('/integrations/outlook-calendar/sync/status'),
  },

  zoom: {
    getAuthUrl: async (options?: {
      orgSlug?: string
      returnPath?: string
    }): Promise<{ authUrl: string }> => {
      const query = new URLSearchParams()
      if (options?.orgSlug) query.set('orgSlug', options.orgSlug)
      if (options?.returnPath) query.set('returnPath', options.returnPath)
      const queryString = query.toString()
      return apiClient(
        await withDesktopOAuthParams(
          `/integrations/zoom/auth/url${queryString ? `?${queryString}` : ''}`,
        ),
      )
    },

    handleCallback: (
      code: string,
      state: string,
    ): Promise<{ success: boolean; message: string; returnPath: string }> =>
      apiClient('/integrations/zoom/auth/callback', {
        method: 'POST',
        body: { code, state },
      }),

    getConnection: (
      orgSlug?: string,
    ): Promise<{
      id: string
      name: string
      accountEmail: string | null
      status: string
    }> => {
      const query = orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : ''
      return apiClient(`/integrations/zoom/connection${query}`)
    },

    disconnect: (orgSlug?: string): Promise<void> => {
      const query = orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : ''
      return apiClient(`/integrations/zoom/connection${query}`, {
        method: 'DELETE',
      })
    },
  },

  slack: {
    getWorkspace: (): Promise<{ workspace: SlackWorkspaceConnection | null }> =>
      apiClient('/integrations/slack/workspace'),

    getInstallUrl: async (
      returnPath?: string,
    ): Promise<{ url: string; state: string }> => {
      const query = new URLSearchParams()
      if (returnPath) query.set('returnPath', returnPath)
      const queryString = query.toString()
      return apiClient(
        await withDesktopOAuthParams(
          `/integrations/slack/install${queryString ? `?${queryString}` : ''}`,
        ),
      )
    },

    disconnectWorkspace: (): Promise<{
      success: boolean
      workspace: SlackWorkspaceConnection
    }> =>
      apiClient('/integrations/slack/workspace', {
        method: 'DELETE',
      }),

    updateWorkspaceSettings: (
      settings: SlackWorkspaceSettings,
    ): Promise<{ workspace: SlackWorkspaceConnection }> =>
      apiClient('/integrations/slack/workspace/settings', {
        method: 'PATCH',
        body: settings,
      }),

    listChannels: (
      cursor?: string,
    ): Promise<{
      channels: SlackChannelSummary[]
      nextCursor: string | null
    }> => {
      const query = new URLSearchParams()
      if (cursor) query.set('cursor', cursor)
      const queryString = query.toString()
      return apiClient(
        `/integrations/slack/channels${queryString ? `?${queryString}` : ''}`,
      )
    },

    listChannelRoutes: (): Promise<{ routes: SlackChannelRouteSummary[] }> =>
      apiClient('/integrations/slack/channel-routes'),

    createChannelRoute: (body: {
      slackChannelId: string
      slackChannelName: string
      projectId?: string
      eventTypes: string[]
    }): Promise<{ route: SlackChannelRouteSummary }> =>
      apiClient('/integrations/slack/channel-routes', {
        method: 'POST',
        body,
      }),

    updateChannelRoute: (
      routeId: string,
      body: {
        projectId?: string | null
        eventTypes?: string[]
        enabled?: boolean
      },
    ): Promise<{ route: SlackChannelRouteSummary }> =>
      apiClient(`/integrations/slack/channel-routes/${routeId}`, {
        method: 'PATCH',
        body,
      }),

    deleteChannelRoute: (routeId: string): Promise<{ success: boolean }> =>
      apiClient(`/integrations/slack/channel-routes/${routeId}`, {
        method: 'DELETE',
      }),

    testChannelRoute: (
      routeId: string,
    ): Promise<{ success: boolean; error?: string }> =>
      apiClient(`/integrations/slack/channel-routes/${routeId}/test`, {
        method: 'POST',
      }),

    getLink: (
      slackTeamId?: string,
    ): Promise<{ link: SlackUserLinkConnection | null }> => {
      const query = new URLSearchParams()
      if (slackTeamId) query.set('slackTeamId', slackTeamId)
      const queryString = query.toString()
      return apiClient(
        `/integrations/slack/link${queryString ? `?${queryString}` : ''}`,
      )
    },

    completeLink: (
      state: string,
      preferredOpenTarget?: 'auto' | 'app' | 'web',
    ): Promise<{ link: SlackUserLinkConnection }> =>
      apiClient('/integrations/slack/link/complete', {
        method: 'POST',
        body: {
          state,
          preferredOpenTarget,
        },
      }),

    disconnectLink: (slackTeamId?: string): Promise<{ revokedCount: number }> =>
      apiClient('/integrations/slack/link/disconnect', {
        method: 'POST',
        body: {
          slackTeamId,
        },
      }),
  },

  github: {
    getInstallUrl: async (
      returnPath?: string,
    ): Promise<{ url: string; state: string }> => {
      const query = new URLSearchParams()
      if (returnPath) query.set('returnPath', returnPath)
      const queryString = query.toString()
      return apiClient(
        await withDesktopOAuthParams(
          `/integrations/github/install${queryString ? `?${queryString}` : ''}`,
        ),
      )
    },

    // Per-user GitHub OAuth (branch/PR creation acts as the user)
    getUserConnectUrl: async (
      returnPath?: string,
      login?: string,
    ): Promise<{ url: string }> => {
      const query = new URLSearchParams()
      if (returnPath) query.set('returnPath', returnPath)
      if (login?.trim()) query.set('login', login.trim())
      const queryString = query.toString()
      return apiClient(
        await withDesktopOAuthParams(
          `/integrations/github/user/connect${queryString ? `?${queryString}` : ''}`,
        ),
      )
    },

    listUserConnections: (): Promise<GitHubUserConnection[]> =>
      apiClient('/integrations/github/user/connections'),

    getUserStatus: (): Promise<{
      connected: boolean
      login?: string
      avatarUrl?: string
      connectedAt?: string
      status?: string
    }> => apiClient('/integrations/github/user/status'),

    disconnectUser: (connectionId: string): Promise<{ success: boolean }> =>
      apiClient(`/integrations/github/user/connections/${connectionId}`, {
        method: 'DELETE',
      }),

    listInstallations: (): Promise<GitHubInstallationConnection[]> =>
      apiClient('/integrations/github/installations'),

    syncRepositories: (
      installationId: string,
    ): Promise<{ repositories: GitHubRepositoryConnection[] }> =>
      apiClient(
        `/integrations/github/installations/${installationId}/sync-repositories`,
        {
          method: 'POST',
        },
      ),

    disconnectInstallation: (
      installationId: string,
    ): Promise<{ success: boolean }> =>
      apiClient(`/integrations/github/installations/${installationId}`, {
        method: 'DELETE',
      }),

    listRepositories: (): Promise<GitHubRepositoryConnection[]> =>
      apiClient('/integrations/github/repositories'),

    listRepositoryBranches: (
      repositoryId: string,
    ): Promise<{ defaultBranch: string; branches: { name: string }[] }> =>
      apiClient(`/integrations/github/repositories/${repositoryId}/branches`),

    listRepositoryUserConnections: (
      repositoryId: string,
    ): Promise<GitHubRepositoryUserConnections> =>
      apiClient(
        `/integrations/github/repositories/${repositoryId}/user-connections`,
      ),

    listProjectRepositories: (
      projectId: string,
    ): Promise<ProjectGitHubRepositoryConnection[]> =>
      apiClient(`/integrations/github/projects/${projectId}/repositories`),

    linkProjectRepository: (
      projectId: string,
      data: {
        repositoryId: string
        isDefault?: boolean
        defaultBaseBranch?: string
        branchNamePattern?: string
        prTitleTemplate?: string
        prBodyTemplate?: string
      },
    ): Promise<ProjectGitHubRepositoryConnection> =>
      apiClient(`/integrations/github/projects/${projectId}/repositories`, {
        method: 'POST',
        body: data,
      }),

    updateProjectRepository: (
      projectId: string,
      repositoryId: string,
      data: {
        automationSettings?: GitHubRepoAutomationSettings
      },
    ): Promise<ProjectGitHubRepositoryConnection> =>
      apiClient(
        `/integrations/github/projects/${projectId}/repositories/${repositoryId}`,
        {
          method: 'PATCH',
          body: data,
        },
      ),

    unlinkProjectRepository: (
      projectId: string,
      repositoryId: string,
    ): Promise<{ success: boolean }> =>
      apiClient(
        `/integrations/github/projects/${projectId}/repositories/${repositoryId}`,
        {
          method: 'DELETE',
        },
      ),

    listTaskLinks: (taskId: string): Promise<TaskGitHubLinkConnection[]> =>
      apiClient(`/integrations/github/tasks/${taskId}/links`),

    generateBranchName: (
      taskId: string,
      data: {
        repositoryId?: string
        projectRepositoryId?: string
        pattern?: string
      },
    ): Promise<{
      branchName: string
      baseBranch: string | null
      repository: GitHubRepositoryConnection | null
    }> =>
      apiClient(`/integrations/github/tasks/${taskId}/branch-name`, {
        method: 'POST',
        body: data,
      }),

    createBranch: (
      taskId: string,
      data: {
        repositoryId?: string
        projectRepositoryId?: string
        pattern?: string
        branchName?: string
        baseBranch?: string
        githubConnectionId?: string
      },
    ): Promise<{
      branchName: string
      baseBranch: string
      link: TaskGitHubLinkConnection
    }> =>
      apiClient(`/integrations/github/tasks/${taskId}/branch`, {
        method: 'POST',
        body: data,
      }),

    createPullRequest: (
      taskId: string,
      data: {
        repositoryId: string
        projectRepositoryId?: string
        branchName: string
        baseBranch?: string
        title?: string
        body?: string
        draft?: boolean
        githubConnectionId?: string
      },
    ): Promise<{ pullRequest: unknown; link: TaskGitHubLinkConnection }> =>
      apiClient(`/integrations/github/tasks/${taskId}/pull-request`, {
        method: 'POST',
        body: data,
      }),
  },
}
